// DispatchDeck — saved-place categories (TASK-9540)
//
// The first DATA LAYER of the map builder (TASK-4789). A saved place
// (tenants/{tid}/savedLocations/{id}) carries ONE `categoryId`; a category is
// name + SHAPE + COLOR. The map draws each saved place as its category's shape,
// and the key switches categories on and off. Pure module: no DOM, no
// Firestore — tests/task9540-place-categories.unit.cjs runs it in a sandbox.
//
// RULES (COD 2026-10-01 + Chris):
//   • Shared tenant-wide, never per-customer (a water depot or a landfill is
//     everybody's). Stored in tenants/{tid}/waypointCategories/{id}.
//   • The eight STARTERS live here in code with stable ids. A Firestore doc
//     with a starter's id overrides it; `hidden: true` = that starter deleted.
//     Nothing is seeded, so a tenant that never opens the manager writes nothing.
//   • 'other' is the fallback and cannot be deleted. A place with no category,
//     or whose category was deleted, reads as Other.
//   • Shapes and colors are ALLOWLISTS, mirrored in firestore.rules
//     placeCatOk — change both together (the unit test checks they agree).
//     Plain geometric shapes only: silhouettes blur at 390px in the sun.
//   • Colors stay clear of the job-status fills (blue/amber/green/gray), the
//     Last-dispatched navy, Fleet purple and the Equipment age colors, and all
//     carry a white stroke legibly.
//   • Pickup / drop-off are NOT categories — they are a per-job stop role.
//   • A job stop never shows its category as a fill: status colors own the
//     fill; the category is a badge.

// Marker paths are in a -1..1 box (google.maps.Symbol path, scaled by the
// caller). The swatch SVG uses the same path in a 24x24 viewBox.
export const PLACE_SHAPES = {
  diamond:  { label: 'Diamond',       path: 'M 0,-1 1,0 0,1 -1,0 z' },
  square:   { label: 'Square',        path: 'M -0.8,-0.8 0.8,-0.8 0.8,0.8 -0.8,0.8 z' },
  triangle: { label: 'Triangle',      path: 'M 0,-1 1,0.8 -1,0.8 z' },
  down:     { label: 'Down triangle', path: 'M -1,-0.8 1,-0.8 0,1 z' },
  hexagon:  { label: 'Hexagon',       path: 'M -0.5,-0.87 0.5,-0.87 1,0 0.5,0.87 -0.5,0.87 -1,0 z' },
  star:     { label: 'Star',          path: 'M 0,-1 0.24,-0.33 0.95,-0.31 0.38,0.12 0.59,0.81 0,0.4 -0.59,0.81 -0.38,0.12 -0.95,-0.31 -0.24,-0.33 z' },
};
export const PLACE_SHAPE_IDS = Object.keys(PLACE_SHAPES);

export const PLACE_COLORS = [
  { hex: '#0E7490', label: 'Teal' },
  { hex: '#BE123C', label: 'Red' },
  { hex: '#A21CAF', label: 'Magenta' },
  { hex: '#3F6212', label: 'Olive' },
  { hex: '#9A3412', label: 'Rust' },
  { hex: '#0C4A6E', label: 'Deep blue' },
  { hex: '#44403C', label: 'Stone' },
];
export const PLACE_COLOR_HEXES = PLACE_COLORS.map((c) => c.hex);

export const OTHER_CATEGORY_ID = 'other';
export const PLACE_CAT_NAME_MAX = 40;

// Order here is the order on screen. Other is last and is the fallback.
export const STARTER_CATEGORIES = [
  { id: 'yard',     name: 'Yard / Shop',         shape: 'square',   color: '#44403C' },
  { id: 'wellpad',  name: 'Well pad',            shape: 'triangle', color: '#BE123C' },
  { id: 'water',    name: 'Water depot',         shape: 'diamond',  color: '#0C4A6E' },
  { id: 'disposal', name: 'Disposal / Landfill', shape: 'down',     color: '#3F6212' },
  { id: 'pit',      name: 'Pit / Gravel',        shape: 'hexagon',  color: '#9A3412' },
  { id: 'custyard', name: 'Customer yard',       shape: 'square',   color: '#0E7490' },
  { id: 'fuel',     name: 'Fuel',                shape: 'star',     color: '#A21CAF' },
  { id: OTHER_CATEGORY_ID, name: 'Other',        shape: 'diamond',  color: '#0E7490' },
];
const STARTER_IDS = new Set(STARTER_CATEGORIES.map((c) => c.id));

export function isStarterCategoryId(id) {
  return STARTER_IDS.has(id);
}

// Shape check for a category id carried on a JOB STOP (jobs/{id}.waypoints[].
// categoryId). Starter ids and Firestore auto-ids both pass; anything else is
// dropped rather than written. Display always resolves it through
// categoryForPlace, so an unknown id reads as Other — it never reaches markup.
export function isValidStopCategoryId(v) {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v);
}

// Validate what a user typed/picked. Returns { ok, value, error } where value
// is exactly what may be written (rules placeCatOk accepts it).
export function validatePlaceCategory(input) {
  const name = String((input && input.name) || '').replace(/\s+/g, ' ').trim();
  const shape = input && input.shape;
  const color = input && input.color;
  if (!name) return { ok: false, error: 'Give the category a name.' };
  if (name.length > PLACE_CAT_NAME_MAX) return { ok: false, error: `Keep the name under ${PLACE_CAT_NAME_MAX + 1} letters.` };
  if (!PLACE_SHAPE_IDS.includes(shape)) return { ok: false, error: 'Pick a shape.' };
  if (!PLACE_COLOR_HEXES.includes(color)) return { ok: false, error: 'Pick a color.' };
  return { ok: true, value: { name, shape, color } };
}

// Starters overlaid with the tenant's docs. `docs` = [{ id, name, shape,
// color, hidden? }]. A doc whose shape/color is off the allowlist (written
// before a list change, or by hand) falls back to the starter's / Other's
// look instead of drawing something unknown. Hidden starters drop out, except
// Other, which always exists. Returns [{ id, name, shape, color, starter }].
export function mergePlaceCategories(docs) {
  const byId = new Map();
  for (const d of (Array.isArray(docs) ? docs : [])) {
    if (d && typeof d.id === 'string' && d.id) byId.set(d.id, d);
  }
  const other = STARTER_CATEGORIES.find((c) => c.id === OTHER_CATEGORY_ID);
  const clean = (d, base) => ({
    name: (typeof d.name === 'string' && d.name.trim()) ? d.name.trim().slice(0, PLACE_CAT_NAME_MAX) : base.name,
    shape: PLACE_SHAPE_IDS.includes(d.shape) ? d.shape : base.shape,
    color: PLACE_COLOR_HEXES.includes(d.color) ? d.color : base.color,
  });
  const out = [];
  for (const s of STARTER_CATEGORIES) {
    if (s.id === OTHER_CATEGORY_ID) continue;
    const d = byId.get(s.id);
    if (d && d.hidden === true) continue;
    out.push({ id: s.id, ...(d ? clean(d, s) : { name: s.name, shape: s.shape, color: s.color }), starter: true });
  }
  const custom = [];
  for (const [id, d] of byId) {
    if (STARTER_IDS.has(id) || d.hidden === true) continue;
    custom.push({ id, ...clean(d, other), starter: false });
  }
  custom.sort((a, b) => a.name.localeCompare(b.name));
  out.push(...custom);
  const od = byId.get(OTHER_CATEGORY_ID);
  out.push({ id: OTHER_CATEGORY_ID, ...(od ? clean(od, other) : { name: other.name, shape: other.shape, color: other.color }), starter: true });
  return out;
}

// The category a place reads as. Missing / deleted category -> Other.
export function categoryForPlace(place, cats) {
  const list = Array.isArray(cats) ? cats : [];
  const id = place && typeof place.categoryId === 'string' ? place.categoryId : '';
  return (id && list.find((c) => c.id === id))
    || list.find((c) => c.id === OTHER_CATEGORY_ID)
    || STARTER_CATEGORIES[STARTER_CATEGORIES.length - 1];
}

// Name-based SUGGESTION for a place with no category. Never applied on its own:
// the manager shows it and the user confirms each one. Returns a starter id or
// null. Word-boundary matches only, first rule wins, so "Hess fuel yard" is
// Fuel (more specific) rather than Yard, and "Customer yard" beats plain Yard.
const SUGGEST_RULES = [
  ['disposal', /\b(swd|salt\s*water\s*disposal|disposal|landfill|land\s*fill|dump|transfer\s*station)\b/i],
  ['water',    /\b(water\s*(depot|station|well|stand|sales)?|fresh\s*water|frac\s*pond|pond)\b/i],
  ['fuel',     /\b(fuel|cardlock|card\s*lock|diesel|gas\s*station|truck\s*stop)\b/i],
  ['pit',      /\b(pit|gravel|scoria|quarry|sand|aggregate|rock)\b/i],
  ['wellpad',  /\b(well\s*pad|pad|rig|well|wellsite|well\s*site|location\s*#?\d+)\b/i],
  ['custyard', /\b(customer\s*yard|their\s*yard)\b/i],
  ['yard',     /\b(yard|shop|terminal|our\s*yard|home\s*base)\b/i],
];
export function suggestCategoryId(name) {
  const s = String(name || '');
  if (!s.trim()) return null;
  for (const [id, re] of SUGGEST_RULES) if (re.test(s)) return id;
  return null;
}

// Places with no categoryId that have a suggestion, for the confirm list.
export function placeCategorySuggestions(places, cats) {
  const live = new Set((Array.isArray(cats) ? cats : []).map((c) => c.id));
  const out = [];
  for (const p of (Array.isArray(places) ? places : [])) {
    if (!p || (typeof p.categoryId === 'string' && p.categoryId)) continue;
    const id = suggestCategoryId(p.name);
    if (id && live.has(id)) out.push({ placeId: p.id, name: p.name || '', categoryId: id });
  }
  return out;
}

// Map key filter: which plotted places stay on with these categories hidden.
export function filterPlacesByHiddenCategories(places, cats, hiddenIds) {
  const hidden = hiddenIds instanceof Set ? hiddenIds : new Set(hiddenIds || []);
  if (!hidden.size) return (Array.isArray(places) ? places : []).slice();
  return (Array.isArray(places) ? places : []).filter((p) => !hidden.has(categoryForPlace(p, cats).id));
}

// Count places per category id (for the key and the manager).
export function countPlacesByCategory(places, cats) {
  const out = {};
  for (const p of (Array.isArray(places) ? places : [])) {
    const id = categoryForPlace(p, cats).id;
    out[id] = (out[id] || 0) + 1;
  }
  return out;
}

// Inline SVG swatch. `color` and `shape` are re-checked against the allowlists
// here, so a swatch can never carry a stored string into markup.
export function placeShapeSvg(shape, color, size) {
  const sh = PLACE_SHAPES[shape] ? shape : 'diamond';
  const fill = PLACE_COLOR_HEXES.includes(color) ? color : '#0E7490';
  const px = Number.isFinite(size) && size > 0 ? Math.round(size) : 18;
  return `<svg width="${px}" height="${px}" viewBox="-1.25 -1.25 2.5 2.5" aria-hidden="true" focusable="false" style="flex-shrink:0;vertical-align:middle;"><path d="${PLACE_SHAPES[sh].path}" fill="${fill}" stroke="#fff" stroke-width="0.22" stroke-linejoin="round"/></svg>`;
}
